import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Check, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { ZODIAC_SIGNS, readMySign, writeMySign } from "@/lib/readoo";
import { apiUpdateZodiac, getHoroscopeSign } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { BackButton } from "@/components/BackButton";

const VIOLET = "#8b5cf6";

export default function MySign() {
  const navigate = useNavigate();
  const { user, setUser } = useAuth();
  const [selected, setSelected] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    document.title = "Il mio segno · Readoo";
    setSelected((user && user.zodiac_sign) || readMySign());
  }, [user]);

  const choose = async (key) => {
    const isDeselect = selected === key;
    const nextKey = isDeselect ? null : key;
    setSelected(nextKey);
    writeMySign(nextKey);
    if (user) {
      setSaving(true);
      try {
        const { user: u } = await apiUpdateZodiac(nextKey);
        if (u) setUser(u);
      } catch {} finally { setSaving(false); }
    }
    if (isDeselect) {
      toast.success("Segno rimosso");
    } else {
      toast.success(`Segno impostato: ${ZODIAC_SIGNS.find((s) => s.key === key)?.name}`);
    }
  };

  const goToMyHoroscope = async () => {
    if (!selected) return;
    try {
      const r = await getHoroscopeSign(selected);
      if (r?.sign?.article_id) navigate(`/articolo/${r.sign.article_id}/oroscopo-${selected}`);
      else navigate("/oroscopo");
    } catch { navigate("/oroscopo"); }
  };

  return (
    <div className="max-w-[900px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <BackButton />
      <div className="flex items-center gap-2 mb-2">
        <span className="font-mono text-xs font-bold uppercase tracking-wider" style={{ color: VIOLET }}>Oroscopo</span>
      </div>
      <h1 className="font-serif text-4xl sm:text-5xl font-semibold text-onSurface">Il mio segno</h1>
      <p className="text-subtle mt-2">Scegli il tuo segno zodiacale: lo mostreremo in cima alla home ogni giorno. Tocca di nuovo il segno selezionato per rimuoverlo.</p>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 mt-8">
        {ZODIAC_SIGNS.map((s) => {
          const active = selected === s.key;
          return (
            <button
              key={s.key}
              data-testid={`sign-option-${s.key}`}
              onClick={() => choose(s.key)}
              className="relative text-left rounded-2xl border p-4 transition-colors"
              style={active
                ? { borderColor: VIOLET, backgroundColor: "rgba(139,92,246,0.12)" }
                : { borderColor: "rgb(var(--hair))", backgroundColor: "rgb(var(--surface-2))" }}
            >
              {active && <span className="absolute top-3 right-3 w-5 h-5 rounded-full flex items-center justify-center text-white" style={{ backgroundColor: VIOLET }}><Check size={13} /></span>}
              <span className="text-3xl">{s.emoji}</span>
              <p className="mt-2 font-serif text-base font-semibold text-onSurface">{s.name}</p>
              <p className="text-[11px] font-mono text-subtle">{s.dates}</p>
            </button>
          );
        })}
      </div>

      <div className="mt-8 flex flex-wrap gap-3">
        <button
          onClick={goToMyHoroscope}
          disabled={!selected || saving}
          data-testid="mysign-go-button"
          className="inline-flex items-center gap-2 px-5 py-3 rounded-full text-sm font-semibold text-white disabled:opacity-50 transition-colors"
          style={{ backgroundColor: VIOLET }}
        >
          Vedi il mio oroscopo <ArrowRight size={16} />
        </button>
        <Link to="/oroscopo" className="inline-flex items-center gap-2 px-5 py-3 rounded-full text-sm font-semibold border border-hair bg-surfaceSecondary text-onSurface hover:border-brand/50 transition-colors">
          Tutti i segni
        </Link>
      </div>
    </div>
  );
}
